import apiClient from './client';
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenResponse,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  SecuritySettingsResponse,
  User,
  OAuthProvidersResponse,
  LinkedAccountsResponse,
} from './types';

// Auth API calls
export const authApi = {
  // Login with email/password
  login: async (data: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', data);
    return response.data;
  },

  // Register new user
  register: async (data: RegisterRequest): Promise<ApiResponse<User>> => {
    const response = await apiClient.post<ApiResponse<User>>('/auth/register', data);
    return response.data;
  },

  // Refresh access token
  refresh: async (refreshToken: string): Promise<ApiResponse<RefreshTokenResponse>> => {
    const response = await apiClient.post<ApiResponse<RefreshTokenResponse>>('/auth/refresh', {
      refreshToken,
    });
    return response.data;
  },

  // Logout
  logout: async (): Promise<ApiResponse<void>> => {
    const response = await apiClient.post<ApiResponse<void>>('/auth/logout');
    return response.data;
  },

  // Get current user
  getMe: async (): Promise<ApiResponse<User>> => {
    const response = await apiClient.get<ApiResponse<User>>('/auth/me');
    return response.data;
  },

  // Enable 2FA - Get QR code and secret
  enable2FA: async (): Promise<ApiResponse<TwoFactorSetupResponse>> => {
    const response = await apiClient.post<ApiResponse<TwoFactorSetupResponse>>('/auth/2fa/enable');
    return response.data;
  },

  // Verify 2FA code
  verify2FA: async (data: TwoFactorVerifyRequest): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await apiClient.post<ApiResponse<{ success: boolean }>>(
      '/auth/2fa/verify',
      data
    );
    return response.data;
  },

  // Disable 2FA
  disable2FA: async (data: TwoFactorVerifyRequest): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await apiClient.post<ApiResponse<{ success: boolean }>>(
      '/auth/2fa/disable',
      data
    );
    return response.data;
  },

  // Get security settings and active sessions
  getSecuritySettings: async (): Promise<ApiResponse<SecuritySettingsResponse>> => {
    const response = await apiClient.get<ApiResponse<SecuritySettingsResponse>>(
      '/auth/security'
    );
    return response.data;
  },

  // Revoke a session
  revokeSession: async (sessionId: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete<ApiResponse<void>>(`/auth/sessions/${sessionId}`);
    return response.data;
  },

  // OAuth / External Authentication

  // Get list of available OAuth providers
  getOAuthProviders: async (): Promise<ApiResponse<OAuthProvidersResponse>> => {
    const response = await apiClient.get<ApiResponse<OAuthProvidersResponse>>(
      '/auth/oauth/providers'
    );
    return response.data;
  },

  // Get linked external accounts for current user
  getLinkedAccounts: async (): Promise<ApiResponse<LinkedAccountsResponse>> => {
    const response = await apiClient.get<ApiResponse<LinkedAccountsResponse>>(
      '/auth/oauth/accounts'
    );
    return response.data;
  },

  // Link an external OAuth account to current user
  linkOAuthAccount: async (provider: string): Promise<void> => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    window.location.href = `${apiBaseUrl}/api/v1/auth/oauth/${provider.toLowerCase()}/link`;
  },

  // Request a password reset email
  requestPasswordReset: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/forgot-password', { email });
    return response.data;
  },

  // Confirm a password reset using the token from the email link
  confirmPasswordReset: async (token: string, newPassword: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  // Unlink an external OAuth account
  unlinkOAuthAccount: async (
    provider: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      `/auth/oauth/${provider.toLowerCase()}/unlink`
    );
    return response.data;
  },
};
